import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

// Read/Update tenant settings (M3.5/M3.6).
//
// GET — admin и employee читают (видеть бюджет и kill-switch нужно обоим,
//        чтобы понимать причины status='skipped' у сообщений).
// PATCH — admin-only, server даст 403 для employee. UI префлайт-блокирует
//          Switch для employee на основе user.role в auth/store.
//
// Бюджет (sms_budget) — снимок счётчиков Redis за текущий месяц UTC.
// staleTime 30 сек: чтобы свежий kill-switch / quota_exhausted видеть быстро,
// но не дёргать сервер на каждый рендер.
export type TenantSettings = components['schemas']['TenantSettings'];
export type TenantSettingsUpdate = components['schemas']['TenantSettingsUpdate'];
export type SmsBudgetStatus = components['schemas']['SmsBudgetStatus'];
export type PlatformContacts = components['schemas']['PlatformContacts'];

const SETTINGS_KEY = ['tenant', 'settings'] as const;

export function useTenantSettingsQuery() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.get<TenantSettings>('/api/v1/admin/tenant/settings'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// M5.7 — мягкая блокировка оптики суперадмином.
//
// `is_active=false` → мутации отбиваются backend'ом (403), но чтение доступно.
// Хук — единая точка правды для UI-слоя: баннер «Работа приостановлена»,
// дизейбл главных кнопок и сеть безопасности на 403. `is_active === false`
// (строгое сравнение) защищает от ложного «заблокирован» при ещё не
// загруженных / неполных settings (undefined !== false).
export interface TenantBlockedState {
  isBlocked: boolean;
  contacts: PlatformContacts | null;
}

export function useTenantBlocked(): TenantBlockedState {
  const { data } = useTenantSettingsQuery();
  return {
    isBlocked: data?.is_active === false,
    contacts: data?.platform_contacts ?? null,
  };
}

export function useUpdateTenantSettingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['tenant', 'settings', 'update'],
    mutationFn: (input: TenantSettingsUpdate) =>
      api.patch<TenantSettings>('/api/v1/admin/tenant/settings', input),
    onSuccess: (updated) => {
      qc.setQueryData(SETTINGS_KEY, updated);
    },
  });
}
